/**
 * When an edit form's Save button is enabled.
 *
 * - Kept apart from `SaveButton.tsx`: `react/only-export-components` forbids mixing exports.
 */

/** What a Save button needs to know. */
export type SaveButtonState = {
  canSubmit: boolean;
  isSubmitting: boolean;
  isUnchanged: boolean;
};

/**
 * The `form.Subscribe` selector that produces it.
 *
 * - Reads `isDefaultValue`, not `isDirty`: `isDirty` latches, so a reverted edit would keep Save on.
 * - Compares mounted fields only, so a hidden field cannot make the form look changed.
 */
export const selectSaveButtonState = (state: {
  canSubmit: boolean;
  isSubmitting: boolean;
  isDefaultValue: boolean;
}): SaveButtonState => ({
  canSubmit: state.canSubmit,
  isSubmitting: state.isSubmitting,
  isUnchanged: state.isDefaultValue,
});

/**
 * Whether Save is disabled.
 *
 * - `isSubmitting` is needed: design's `Button` never disables on `isLoading`.
 */
export const isSaveDisabled = ({
  canSubmit,
  isSubmitting,
  isUnchanged,
}: SaveButtonState): boolean => isUnchanged || isSubmitting || !canSubmit;
